import React, { useEffect, useState } from 'react';
import ReactDOM from 'react-dom/client';
import App from './App.jsx';
import Inbox from './screens/Inbox.jsx';
import { getSupabase } from './lib/supabase.js';
import './styles/tokens.css';
// Mobile rules last so they win at equal specificity. Every rule inside is
// gated behind @media (max-width:480px) — desktop is unaffected.
import './styles/mobile.css';

// Public API: window.FinOpsInvestment.mount(selectorOrElement, options?)
// - In production: the existing vanilla index.html calls this when the
//   user switches to the Investment tab.
// - In dev (vite dev server): the local index.html in this folder calls
//   mount('#root') automatically below.

const FinOpsInvestment = {
  /**
   * Mount the React app into a target DOM node.
   * @param {string|HTMLElement} target - CSS selector or element
   * @returns {ReturnType<typeof ReactDOM.createRoot> | null}
   */
  mount(target) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) {
      console.error('[FinOpsInvestment] mount target not found:', target);
      return null;
    }
    // The .investment-root class scopes the design-system CSS variables to this
    // subtree so they don't conflict with the existing app's CSS variables.
    el.classList.add('investment-root');

    const root = ReactDOM.createRoot(el);
    root.render(
      <React.StrictMode>
        <App />
      </React.StrictMode>
    );
    return root;
  },

  /**
   * Mount just the Inbox screen as a standalone island — used by the
   * parent dashboard's "Inbox" top-level tab. Has its own React root so
   * it doesn't share the URL-hash routing of the main Investment app.
   */
  mountInbox(target) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) {
      console.error('[FinOpsInvestment] mountInbox target not found:', target);
      return null;
    }
    el.classList.add('investment-root');
    const root = ReactDOM.createRoot(el);
    root.render(
      <React.StrictMode>
        <InboxStandalone />
      </React.StrictMode>
    );
    return root;
  },

  /** Version of the bundle. Bumped on real releases. */
  version: '0.1.0',
};

// Standalone wrapper: resolves the current profile from window.sb, renders
// the Inbox screen with a no-op navigate (so it hides the back button).
function InboxStandalone() {
  const [profile, setProfile] = useState(null);
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const sb = getSupabase();
      if (!sb) return;
      try {
        const { data: { user } } = await sb.auth.getUser();
        if (!user) return;
        const { data } = await sb.from('profiles').select('id, member_id, role').eq('id', user.id).single();
        if (!cancelled) setProfile(data || { id: user.id, role: 'admin' });
      } catch (e) {
        if (!cancelled) setProfile({ role: 'admin' });
      }
    })();
    return () => { cancelled = true; };
  }, []);
  return <Inbox profile={profile} navigate={null} />;
}

if (typeof window !== 'undefined') {
  window.FinOpsInvestment = FinOpsInvestment;
}

// Dev convenience: when run via vite dev server, auto-mount to #root.
// In production (IIFE bundle), this if-block doesn't fire because there's
// no #root in the parent index.html — the parent calls mount() explicitly.
if (typeof window !== 'undefined' && document.querySelector('#root')) {
  // Defer to next tick so the rest of the IIFE finishes setting up.
  queueMicrotask(() => FinOpsInvestment.mount('#root'));
}

export default FinOpsInvestment;
